export const BillingCommandBillingRegimeList = [
  "legacy_v1",
  "stripe_v2",
  "modular_2026",
] as const;

export type BillingCommandBillingRegime =
  (typeof BillingCommandBillingRegimeList)[number];

export const BillingCommandFamilyList = [
  "create_reactivate",
  "trial_lifecycle",
  "product_set_change",
  "pause_cancel_release",
  "subscription_lifecycle",
  "scheduled_wakeup",
  "reconciliation",
  "repair_sweep",
  "admin_schedule",
  "agency_lifecycle",
  "provider_confirmation",
  "shopify_cap_approval",
] as const;

export const ProviderConfirmationActionList = [
  "payment_succeeded",
  "expiry_check",
] as const;

export type ProviderConfirmationAction =
  (typeof ProviderConfirmationActionList)[number];

export type BillingCommandFamily = (typeof BillingCommandFamilyList)[number];

export const BillingPolicyCommandFamilyList = [
  "create_reactivate",
  "trial_lifecycle",
  "product_set_change",
  "pause_cancel_release",
  "subscription_lifecycle",
  "scheduled_wakeup",
  "admin_schedule",
  "agency_lifecycle",
] as const;

export type BillingPolicyCommandFamily =
  (typeof BillingPolicyCommandFamilyList)[number];

export const BillingRequestSourceList = [
  "admin_created",
  "admin_modify",
  "self_serve_create",
  "member_api",
  "reactivation_from_pause",
  "scheduled_wakeup",
  "repair",
  "replay",
] as const;

export type BillingRequestSource = (typeof BillingRequestSourceList)[number];

export const BillingCreateReactivateScenarioList = [
  "admin_created",
  "self_serve_create",
  "reactivation_from_pause",
] as const;

export type BillingCreateReactivateScenario =
  (typeof BillingCreateReactivateScenarioList)[number];

/**
 * Admin-schedule scenarios that bootstrap a brand-new subscription rather than
 * mutating an existing one. `admin_modify` is excluded (it requires an existing
 * live sub); `admin_pending_create_amend`/`admin_pending_create_cancel` are
 * excluded because they are recorded amendments folded into the root
 * `admin_create` by the SM-03 resolver and never reach policy resolution.
 * `satisfies` guards against scenario-id typos (e.g. the `admin_created`
 * BillingRequestSource vs the `admin_create` admin-schedule scenario).
 */
const AdminScheduleCreateScenarioList = [
  "admin_create",
  "admin_reactivate",
] as const satisfies readonly BillingAdminScheduleScenario[];

/**
 * Allowlist classifier of scenario ids that may legitimately bootstrap a new
 * Stripe subscription. Per-scenario (not per-family) so mixed-intent families
 * (e.g. admin_schedule) resolve correctly. Fail-closed: unknown scenarios
 * default to mutate-existing semantics and must be listed here to opt into
 * create.
 */
const SCENARIOS_THAT_MAY_CREATE_NEW_SUBSCRIPTION: ReadonlySet<string> = new Set(
  [...BillingCreateReactivateScenarioList, ...AdminScheduleCreateScenarioList]
);

export function scenarioMayCreateNewSubscription(scenarioId: string): boolean {
  return SCENARIOS_THAT_MAY_CREATE_NEW_SUBSCRIPTION.has(scenarioId);
}

export const BillingCommandErrorCodeList = [
  "dispatch_failed",
  "invalid_payload",
  "missing_tier_snapshot",
  "policy_rejected",
  "request_body_drift",
  "stripe_mutation_failed",
  "stripe_non_retryable",
  "customer_tax_location_invalid",
  "stripe_subscription_payment_declined",
  "stripe_subscription_past_due",
  "stripe_subscription_incomplete_expired",
  "stripe_subscription_canceled",
  "stripe_subscription_unpaid",
  "stripe_subscription_paused",
  "billing_provider_not_yet_supported",
  "shopify_approval_timeout",
  "shopify_approval_declined",
  "shopify_non_retryable",
  "shopify_pending_approval_conflict",
  "webhook_timeout",
  "customer_ready_timeout",
  "manual_intervention_required",
  "repair_attempts_exhausted",
  "provider_action_expired",
  "provider_confirmation_pending",
  "provider_confirmation_projection_failed",
  "operator_cancelled",
  "no_live_subscription_to_modify",
  "shopify_app_subscription_not_active",
  "billing_regime_out_of_scope",
] as const;

export type BillingCommandErrorCode =
  (typeof BillingCommandErrorCodeList)[number];

/**
 * User-facing copy for every terminal billing-command error code. Published
 * over the command-update websocket when a command fails without a
 * policy-rejection reason (provider failures, timeouts, ...), so the frontend
 * can show something better than a generic "Billing command failed" toast.
 * `satisfies` forces a new code to ship with copy.
 */
export const BILLING_COMMAND_ERROR_MESSAGES = {
  dispatch_failed: "We couldn't start this billing change. Please try again.",
  invalid_payload:
    "This billing request was invalid. Please refresh the page and try again.",
  missing_tier_snapshot:
    "We couldn't determine your current billing tier. Please try again or contact support.",
  policy_rejected: "This plan change isn't allowed for your subscription.",
  request_body_drift:
    "This billing request changed while it was being processed. Please try again.",
  stripe_mutation_failed:
    "Our payment provider couldn't apply this change. Please try again.",
  stripe_non_retryable:
    "Our payment provider rejected this change. Please contact support if this keeps happening.",
  customer_tax_location_invalid:
    "Your billing address is missing or invalid. Please update your billing details and try again.",
  stripe_subscription_payment_declined:
    "Your payment method was declined. Please update it and try again.",
  stripe_subscription_past_due:
    "Your subscription has a past-due balance. Please settle it before changing your plan.",
  stripe_subscription_incomplete_expired:
    "Your subscription setup expired before payment completed. Please start a new subscription.",
  stripe_subscription_canceled:
    "Your subscription is canceled, so this change can't be applied.",
  stripe_subscription_unpaid:
    "Your subscription is unpaid. Please settle the balance before changing your plan.",
  stripe_subscription_paused:
    "Your subscription is paused, so this change can't be applied.",
  billing_provider_not_yet_supported:
    "This change isn't supported for your billing provider yet.",
  shopify_approval_timeout:
    "The Shopify approval wasn't completed in time. Please try again.",
  shopify_approval_declined: "The Shopify charge was declined.",
  shopify_non_retryable:
    "Shopify rejected this billing change. Please contact support if this keeps happening.",
  shopify_pending_approval_conflict:
    "A previous plan is still awaiting approval in Shopify. Approve or decline it there, then try this change again.",
  webhook_timeout:
    "We didn't receive confirmation from the payment provider in time. Please check your plan before retrying.",
  customer_ready_timeout:
    "Billing setup wasn't completed in time. Please try again.",
  manual_intervention_required:
    "This billing change needs a hand from our team. Please contact support.",
  repair_attempts_exhausted:
    "We couldn't complete this billing change automatically. Please contact support.",
  provider_action_expired:
    "The pending payment action expired. Please try again.",
  provider_confirmation_pending:
    "This change is still awaiting confirmation from the payment provider.",
  provider_confirmation_projection_failed:
    "The change was applied, but we couldn't refresh your billing details. Please refresh the page.",
  operator_cancelled: "This billing change was cancelled by our team.",
  no_live_subscription_to_modify:
    "There's no active subscription to change. Please start a new subscription.",
  shopify_app_subscription_not_active:
    "Your Shopify subscription is no longer active, so it can't be resumed. Start a new plan to continue.",
  billing_regime_out_of_scope:
    "This billing change doesn't apply to your current billing setup. Please contact support.",
} as const satisfies Record<BillingCommandErrorCode, string>;

export function toBillingCommandErrorMessage(
  errorCode: string | null | undefined
): string | undefined {
  if (!errorCode) {
    return undefined;
  }
  return (BILLING_COMMAND_ERROR_MESSAGES as Record<string, string | undefined>)[
    errorCode
  ];
}

export interface BillingCommandRequestedBy {
  id: string;
  type: string;
  // Snapshotted at intake so downstream consumers attribute the command to the
  // identity that issued it, not to whatever the user record says once the
  // command resolves (which can be hours later, or after an email change).
  // Absent on system-issued commands and on commands queued before this field.
  email?: string;
}

export const BillingCadenceList = [
  "monthly",
  "annual",
  "quarterly",
  "six_month",
] as const;

export type BillingCadence = (typeof BillingCadenceList)[number];

export const BillingEffectiveTimingList = ["immediate", "period_end"] as const;

export type BillingEffectiveTiming =
  (typeof BillingEffectiveTimingList)[number];

export const BillingTrialCommandTypeList = [
  "convert_to_paid",
  "expire_trial",
] as const;

export type BillingTrialCommandType =
  (typeof BillingTrialCommandTypeList)[number];

export const BillingTrialLifecycleScenarioList = BillingTrialCommandTypeList;

export type BillingTrialLifecycleScenario =
  (typeof BillingTrialLifecycleScenarioList)[number];

export const BillingProductSetChangeTypeList = [
  "add_module",
  "remove_module",
  "upgrade_smart_pricing_tier",
  "upgrade_to_blue",
] as const;

export type BillingProductSetChangeType =
  (typeof BillingProductSetChangeTypeList)[number];

/**
 * Every change type is a scenario, plus two that ride this family without
 * moving the product set, and so carry no `changeType`:
 * - `cancel_pending_module_removal` — the merchant undo of a module removal
 *   already booked for a future boundary; it addresses a deferred
 *   `remove_module` via a `sourceCommandId` and resolves to a plan with no
 *   provider mutation.
 * - `apply_coupon` — adds an operator coupon against the subscription's
 *   current prices, unchanged. It is a scenario, never a change *type*: a
 *   coupon changes no product set, and keeping it out of the change-type list
 *   stops a client from smuggling it (with a chosen timing) through the
 *   upgrade/downgrade schema, whose `changeType` is drawn from that list.
 */
export const BillingProductSetChangeScenarioList = [
  ...BillingProductSetChangeTypeList,
  "cancel_pending_module_removal",
  "apply_coupon",
] as const;

export type BillingProductSetChangeScenario =
  (typeof BillingProductSetChangeScenarioList)[number];

export const BillingPauseCancelReleaseActionList = [
  "transfer_to_pause",
  "cancel",
  "release_pending_cancellation",
] as const;

/**
 * Provider-level lifecycle verbs the internal (no-provider-mutation) executor
 * understands. Deliberately narrower than BillingPauseCancelReleaseAction:
 * `transfer_to_pause` has no provider effect on either provider today, so it
 * maps to no lifecycle action at all rather than a member here.
 */
export const BillingProviderLifecycleActionList = [
  "cancel",
  "schedule_cancel",
  "release",
] as const;

export type BillingProviderLifecycleAction =
  (typeof BillingProviderLifecycleActionList)[number];

export type BillingPauseCancelReleaseAction =
  (typeof BillingPauseCancelReleaseActionList)[number];

export const BillingPauseCancelReleaseScenarioList =
  BillingPauseCancelReleaseActionList;

export type BillingPauseCancelReleaseScenario =
  (typeof BillingPauseCancelReleaseScenarioList)[number];

export const BillingSubscriptionLifecycleReviewTypeList = [
  "annual_renewal_review",
  "order_tier_review",
  "notice_boundary_review",
] as const;

export type BillingSubscriptionLifecycleReviewType =
  (typeof BillingSubscriptionLifecycleReviewTypeList)[number];

export const BillingSubscriptionLifecycleScenarioList =
  BillingSubscriptionLifecycleReviewTypeList;

export type BillingSubscriptionLifecycleScenario =
  (typeof BillingSubscriptionLifecycleScenarioList)[number];

export const BillingAdminScheduleScenarioList = [
  "admin_create",
  "admin_modify",
  "admin_reactivate",
  "admin_pending_create_amend",
  "admin_pending_create_cancel",
] as const;

export type BillingAdminScheduleScenario =
  (typeof BillingAdminScheduleScenarioList)[number];

export const BillingAgencyLifecycleScenarioList = [
  "add_brand",
  "remove_brand_immediate",
  "remove_brand_scheduled",
  "plan_upgrade",
  "plan_downgrade_scheduled",
  "apply_deferred_brand_removal",
  "apply_deferred_plan_change",
  "cancel_pending_downgrade",
  "cancel_pending_brand_removal",
] as const;

export type BillingAgencyLifecycleScenario =
  (typeof BillingAgencyLifecycleScenarioList)[number];

export const BillingScheduledFollowupCommandTypeList = [
  "trial_expiry",
  "extended_trial_expiry",
  "commitment_boundary_review",
  "annual_renewal_review",
  "tier_upgrade_notice_boundary_review",
  "starter_threshold_upgrade_selection_review",
  "subscription_lifecycle_review",
  "agency_plan_change_due",
  "agency_brand_removal_due",
  "retry_backoff_resume",
  "scheduled_pause_due",
  "scheduled_cancel_due",
  "scheduled_module_removal_due",
  "scheduled_module_trial_cancellation_due",
] as const;

export type BillingScheduledFollowupCommandType =
  (typeof BillingScheduledFollowupCommandTypeList)[number];

export const BillingScheduledWakeupScenarioList =
  BillingScheduledFollowupCommandTypeList;

export type BillingScheduledWakeupScenario =
  (typeof BillingScheduledWakeupScenarioList)[number];

export const BillingScheduledBoundaryReasonList = [
  "trial_expiry",
  "extended_trial_expiry",
  "commitment_boundary",
  "annual_renewal",
  "tier_upgrade_notice_boundary",
  "starter_threshold_upgrade_selection",
  "subscription_lifecycle_review",
  "agency_plan_change_due",
  "agency_brand_removal_due",
  "retry_backoff",
  "scheduled_pause",
  "scheduled_cancel",
  "scheduled_module_removal",
  "scheduled_module_trial_cancellation",
] as const;

export type BillingScheduledBoundaryReason =
  (typeof BillingScheduledBoundaryReasonList)[number];
